/**
 * A page's own head (web plan W5.1; it replaces W0's imperative `useSeo`
 * hook): `<title>`, the SEO `<meta>` / `<link>` tags and the JSON-LD script,
 * rendered as React elements. React 19 puts the title, metas and the
 * canonical link in the document `<head>` and removes them with the page;
 * the JSON-LD script stays where the page renders it, in the body (web plan
 * W4.3), with the id the root Layout's carries.
 *
 * It renders nothing:
 * - on the URL the document was served for, where the SEO server's head
 *   stands (the root's / the route module's `meta`, @/seo/documentHead): the
 *   server built it from the same `@/seo/pageHeads` functions, and its
 *   canonical and robots are final there;
 * - in a route module rendered from its server copy (@/hooks/entityPreload,
 *   @/hooks/routePreload): the route's `meta` owns the head.
 *
 * So it speaks after an in-app navigation, and without the SEO server
 * (`react-router dev`, component tests). The tags follow @/seo/clientHead;
 * tags in the head that React does not own and that duplicate its own are
 * removed after each commit (@/seo/headCleanup, web plan MW5).
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useEntityPreload } from '@/hooks/entityPreload';
import { useRoutePreload } from '@/hooks/routePreload';
import { useRouteLocale } from '@/hooks/useRouteLocale';
import { useServesDocumentHead } from '@/hooks/useServesDocumentHead';
import { useTranslate } from '@/i18n/LocaleLanguage';
import { clientHead, type PageHeadOptions } from '@/seo/clientHead';
import { JSONLD_ELEMENT_ID, scriptJson } from '@/seo/documentHead';
import { removeUnownedHeadTwins } from '@/seo/headCleanup';
import { SITE_NAME } from '@/seo/templates';

export type { PageHeadOptions };

export function PageHead(options: PageHeadOptions) {
  const { pathname } = useLocation();
  // The page's locale route's catalogue (web plan W3.0), as the SEO server's.
  const t = useTranslate();
  const locale = useRouteLocale();
  const entityPreload = useEntityPreload();
  const routePreload = useRoutePreload();
  const serverHeadStands = useServesDocumentHead();
  const renders = entityPreload === null && routePreload === null && !serverHeadStands;
  // After every commit of the page's head: drop the tags React does not own
  // that say the same thing as one it does (a stale canonical or hreflang set
  // left in the head by the document or an earlier page, web plan MW5).
  useEffect(() => {
    if (renders) removeUnownedHeadTwins();
  });
  if (!renders) return null;

  const head = clientHead(options, { pathname, locale, t });
  return (
    <>
      <title>{head.title}</title>
      <meta name="description" content={head.description} />
      {head.robots ? <meta name="robots" content={head.robots} /> : null}
      <link rel="canonical" href={head.canonical} />
      {head.alternates?.map((alternate) => (
        <link key={alternate.hreflang} rel="alternate" hrefLang={alternate.hreflang} href={alternate.url} />
      ))}
      <meta property="og:title" content={head.title} />
      <meta property="og:description" content={head.description} />
      <meta property="og:url" content={head.canonical} />
      <meta property="og:type" content={head.type} />
      <meta property="og:image" content={head.image} />
      {head.imageSize ? (
        <>
          <meta property="og:image:width" content={String(head.imageSize.width)} />
          <meta property="og:image:height" content={String(head.imageSize.height)} />
        </>
      ) : null}
      <meta property="og:image:alt" content={head.imageAlt} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={head.title} />
      <meta name="twitter:description" content={head.description} />
      <meta name="twitter:image" content={head.image} />
      <meta name="twitter:image:alt" content={head.imageAlt} />
      {head.jsonLd ? (
        <script
          id={JSONLD_ELEMENT_ID}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: scriptJson(head.jsonLd) }}
        />
      ) : null}
    </>
  );
}

export default PageHead;
